import _ from 'lodash'
import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { format, isValid } from 'date-fns'

import lang from 'lang'
import offlineTrainingsService from 'services/offline-trainings'
import { AppState } from 'common/store/reducer'
import { Validations } from 'common/validator-types'

import TrainingRecord from './record'

export interface TrainingItem {
  offline_training_id: string | null
  training_attended_at: string | null
}

interface OfflineTrainingType {
  id: string
  name: string
}

/** Nested validation spec for offlineTrainings (e.g. from school form) */
export type OfflineTrainingsValidations = keyof Validations | Record<string, unknown>

interface OwnProps {
  errors?: {
    offlineTrainings?: Record<
      number,
      { offline_training_id?: string; training_attended_at?: string }
    >
  }
  validations?: { offlineTrainings?: OfflineTrainingsValidations }
  changeFormField: (key: string, value: unknown) => void
  validateField: (props: {
    field: string
    value: unknown
    validations: OfflineTrainingsValidations
  }) => void
  trainings: TrainingItem[]
}

const connector = connect(
  (state: AppState) => ({
    user: state.root.user,
    offlineTrainingTypes: state.offlineTrainings.items as OfflineTrainingType[],
  }),
  (dispatch) => ({
    offlineTrainingsRequest: (payload?: unknown) =>
      dispatch(
        offlineTrainingsService.actions.request(
          payload as Parameters<typeof offlineTrainingsService.actions.request>[0],
        ),
      ),
  }),
)

type Props = ConnectedProps<typeof connector> & OwnProps

class TrainingPicker extends React.Component<Props> {
  componentDidMount(): void {
    this.props.offlineTrainingsRequest()
  }

  get usedTrainingTypes(): string[] {
    const { trainings, offlineTrainingTypes } = this.props
    return _.intersection(
      _.map(trainings, 'offline_training_id').filter((id): id is string => id != null),
      _.map(offlineTrainingTypes, 'id'),
    )
  }

  addTrainingHandler = (): (() => void) => {
    return () => {
      const newTrainings = [...this.props.trainings]
      if (newTrainings.length >= this.props.offlineTrainingTypes.length) {
        return
      }
      newTrainings.push({
        offline_training_id: null,
        training_attended_at: null,
      })
      this.props.changeFormField('offlineTrainings', newTrainings)
      this.forceUpdate()
    }
  }

  removeTrainingHandler = (index: number): (() => void) => {
    return () => {
      const newTrainings = _.clone(this.props.trainings)
      newTrainings.splice(index, 1)
      this.props.changeFormField('offlineTrainings', newTrainings)
      if (this.props.validations?.offlineTrainings) {
        this.props.validateField({
          field: 'offlineTrainings',
          value: newTrainings,
          validations: this.props.validations.offlineTrainings,
        })
      }
    }
  }

  trainingChangeHandler = (
    index: number,
    training: TrainingItem,
  ): ((e: React.ChangeEvent<HTMLSelectElement>) => void) => {
    return (e) => {
      const newTrainings = _.clone(this.props.trainings)
      const updated = { ...training, offline_training_id: e.target.value || null }
      newTrainings.splice(index, 1, updated)
      this.props.changeFormField('offlineTrainings', newTrainings)
      if (this.props.validations?.offlineTrainings) {
        this.props.validateField({
          field: 'offlineTrainings',
          value: newTrainings,
          validations: this.props.validations.offlineTrainings,
        })
      }
    }
  }

  dateChangeHandler = (index: number, training: TrainingItem): ((date: Date) => void) => {
    return (date) => {
      if (!isValid(date)) {
        return
      }
      const newTrainings = _.clone(this.props.trainings)
      const updated = {
        ...training,
        training_attended_at: format(new Date(date), 'yyyy-MM-dd'),
      }
      newTrainings.splice(index, 1, updated)
      this.props.changeFormField('offlineTrainings', newTrainings)
      if (this.props.validations?.offlineTrainings) {
        this.props.validateField({
          field: 'offlineTrainings',
          value: newTrainings,
          validations: this.props.validations.offlineTrainings,
        })
      }
    }
  }

  render(): React.ReactNode {
    const { trainings, offlineTrainingTypes } = this.props
    const errors = this.props.errors?.offlineTrainings ?? {}

    return (
      <fieldset className="inline-controls training-picker">
        <label>{lang.dashboard.manageSchoolForm.offlineTrainings}</label>
        <div className="grid-x">
          {_.map(trainings, (training, index) => (
            <TrainingRecord
              key={index}
              index={index}
              offlineTrainingTypes={offlineTrainingTypes}
              usedTrainingTypes={this.usedTrainingTypes}
              onTrainingChangeHandler={this.trainingChangeHandler(index, training)}
              onRemoveTrainingHandler={this.removeTrainingHandler(index)}
              onDateChangeHandler={this.dateChangeHandler(index, training)}
              errors={errors[index]}
              offline_training_id={training.offline_training_id}
              training_attended_at={training.training_attended_at}
            />
          ))}
        </div>
        <button
          className="button hollow small"
          disabled={trainings.length >= offlineTrainingTypes.length}
          type="button"
          onClick={this.addTrainingHandler()}>
          {lang.dashboard.manageSchoolForm.addTraining}
        </button>
      </fieldset>
    )
  }
}

export default connector(TrainingPicker)
